import React, { useCallback, useEffect, useState } from 'react';
import { useProgram, type Program } from 'planby';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { testId } from '@jwp/ott-common/src/utils/common';
import { useTheme } from '../../../../../ThemeContext';

import styles from './EpgProgramItem.module.scss';
import { useUIControls } from '../../../../hooks-react/src/useUIControls';

type Props = {
  program: Program;
  onClick?: (program: Program) => void;
  isActive: boolean;
  compact: boolean;
  disabled: boolean;
  isBaseTimeFormat: boolean;
};

const ProgramItem: React.FC<Props> = ({ program, onClick, isActive, compact, disabled, isBaseTimeFormat }) => {
  const {
    styles: { position },
    formatTime,
    set12HoursTimeFormat,
    isLive,
    isMinWidth,
  } = useProgram({
    program,
    isBaseTimeFormat,
  });

  const { t } = useTranslation('common');
  const { data } = program;
  const { image, title, since, till, id } = data;
  const { universalStyles } = useTheme();
  const { epgTimeFormat } = useUIControls();

  const sinceTime = epgTimeFormat !== '24h'?formatTime(since, set12HoursTimeFormat()).toLowerCase():formatTime(since);
  const tillTime = epgTimeFormat !== '24h'?formatTime(till, set12HoursTimeFormat()).toLowerCase():formatTime(till);

  const showImage = !compact && isMinWidth;
  const showLiveTagInImage = !compact && isMinWidth && isLive;
  const alt = ''; // intentionally empty for a11y, because adjacent text alternative

  const parseTime = (timeStr: string): number => {
    // Create a new Date object for today
    const today = new Date();
    const [time, meridian] = timeStr.split(/(?<=\d)(am|pm)/i); // Split time and AM/PM

    const [hours, minutes] = time.split(':').map(Number);

    let adjustedHours = hours;
    if (meridian?.toLowerCase() === 'pm' && hours !== 12) {
      adjustedHours += 12; // Convert PM hours to 24-hour format
    } else if (meridian?.toLowerCase() === 'am' && hours === 12) {
      adjustedHours = 0; // Handle midnight case
    }

    // Set the parsed time to today's date
    today.setHours(adjustedHours, minutes, 0, 0);
    return today.getTime();
  };

  const calculatePercentage = useCallback((sinceStr: string, tillStr: string): number => {
    const sinceTime = parseTime(sinceStr);
    const tillTime = parseTime(tillStr);
    const now = Date.now();

    if (now < sinceTime) {
      return 0; // If current time is before program start time, return 0%
    }

    const totalDuration = tillTime - sinceTime;
    const elapsed = now - sinceTime;

    return Math.max(0, Math.min((elapsed / totalDuration) * 100, 100)); // Clamp between 0 and 100
  }, []);

  const [percentage, setPercentage] = useState(() => calculatePercentage(sinceTime, tillTime));

  useEffect(() => {
    if (!isLive) return;

    // Function to update the percentage
    const onMinuteChange = () => {
      setPercentage(calculatePercentage(sinceTime, tillTime));
    };

    // Call immediately after the component mounts
    onMinuteChange();

    // Set an interval to update the percentage every 2 minutes (120,000 ms)
    const intervalId = setInterval(onMinuteChange, 120000);

    // Cleanup the interval when the component unmounts
    return () => clearInterval(intervalId);
  }, [calculatePercentage, sinceTime, tillTime, isLive]);

  return (
    <div className={styles.epgProgramBox} style={position} onClick={() => id && onClick && onClick(program)}>
      <div
        className={classNames(id?styles.epgProgram: styles.unselectEpgProgram, {
          [styles.selected]: isActive,
          [styles.live]: isLive,
          [styles.disabled]: disabled,
        })}
        style={{
          width: styles.width,
          backgroundColor: universalStyles.bodyBackgroundColor.backgroundColor,
          backgroundImage: `linear-gradient(to right, ${universalStyles.genre.backgroundColor} ${percentage}%, ${universalStyles.modalBackgroundColors.backgroundColor} ${percentage}%)`,
          backgroundClip: 'padding-box',
        }}
        data-testid={testId(program.data.id)}
      >
        {/* {showImage && <img className={styles.epgProgramImage} src={image} alt={alt} />} */}
        {/* {showLiveTagInImage && <div className={styles.epgLiveTag}>{t('live')}</div>} */}
        <div className={styles.epgProgramContent}>
          {/* {compact && isLive && <div className={styles.epgLiveTag}>{t('live')}</div>} */}
          <h2 className={styles.epgProgramTitle}>{title}</h2>
          <span className={styles.epgProgramText}>
            {sinceTime} - {tillTime}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProgramItem;
